import React from 'react'
import $a from 'axios'
import { message } from 'antd'
import SparkMD5 from 'spark-md5'
import Upload from './upload'
import type { IUpload } from './upload'
import request from '../../utils/request'
import { getImage } from '../../utils/file'

type OssResult = any
interface UploadSuccessResult {
    url: string,
    filename: string,
    dir: string,
    width: number,
    height: number,
    fileSize: number,
    hashMD5: string,
}
interface IProcess {
    event: any,
    key: string,
    file:File
}
interface IUploadImage extends Pick<IUpload, 'disabled' | 'children' | 'multiple' | 'onClick' | 'style' | 'directory'>{
    onStartUpload?:()=>void,
    onEndUpload?:()=>void,
    onBefore?:(result: File[])=> { files: File[], success:boolean } | void,
    onSuccess?: (result: Array<UploadSuccessResult>)=>void,
    onError?: (errors: Array<IUploadErrorMessage>)=> void,
    onUploadProgress?: (process: Array<IProcess>, files: File[])=>void,
    customDir?: string,
    maxSize?: number,
    maxCount?: number,
    hasLimitPixel?: boolean, // 限制像素点
}

interface IUploadErrorMessage{
    type: UPLOAD_ERROR_TYPE,
    message: string,
    file: File,
}

const axios = $a.create()
const CDN_HOST = 'https://zhiyi-image.oss-cn-hangzhou.aliyuncs.com/'
const CDN_DIR = 'ins-selected/'
const GET_TOKEN_API = '/upload/get-oss-upload-token'
const ACCEPT_FILE_TYPES = ['image/jpeg', 'image/pjpeg', 'image/png', 'image/x-png']

export enum UPLOAD_ERROR_TYPE {
    TYPE_ERROR = 1,
    SIZE_ERROR = 2,
    UPLOAD_ERROR = 3,
    COUNT_ERROR = 4,
    PIXEL_SIZE_ERROR = 5,
}

function unionid({ mime, customDir }: {mime?:string, customDir?:string}) {
  return `${customDir || CDN_DIR + new Date().getTime()}_${Math.random().toString(32).substr(2, 10)}${mime || '.png'}`
}
function validFileType(file) {
  return ACCEPT_FILE_TYPES.includes(file.type)
}
function validFileSize(file, maxSize) {
  return file.size < maxSize * 1000000
}
async function validFileWidthHeight(file) {
  const image = await getImage(URL.createObjectURL(file))
  if ((image.width * image.height) > 250000000 || image.width > 30000 || image.height > 30000) {
    return false
  }
  return true
}

const getUploadToOSSSuccess = async (file, customDir, sign, onUploadProgress, onSuccess, onError) => {
  const spark = new SparkMD5()
  spark.append(file)
  const key = unionid({ customDir })
  const hashMD5 = spark.end()
  try {
    await request.formdata(sign.host, {
      OSSAccessKeyId: sign.accessid,
      signature: sign.signature,
      policy: sign.policy,
      success_action_status: 200,
      key,
      file
    }, e => onUploadProgress?.(e, key, file))
    const url = CDN_HOST + key
    const { ImageWidth, ImageHeight, FileSize } = (await axios.get(`${url}?x-oss-process=image/info`)).data
    onSuccess({
      url,
      filename: file.name,
      dir: file?.webkitRelativePath,
      width: ImageWidth.value,
      height: ImageHeight.value,
      fileSize: FileSize.value,
      hashMD5,
    })
  } catch (error) {
    onError?.(UPLOAD_ERROR_TYPE.UPLOAD_ERROR, file)
  }
}

export default function UploadImage({
  maxSize = 10,
  maxCount = 100,
  hasLimitPixel = true,
  onUploadProgress,
  customDir,
  directory,
  multiple,
  children,
  style,
  onClick,
  disabled,
  onSuccess,
  onBefore,
  onStartUpload,
  onEndUpload,
  onError,
}: IUploadImage) {
  const isValid = async (file:File): Promise<IUploadErrorMessage | undefined> => {
    if (!validFileType(file)) {
      return {
        type: UPLOAD_ERROR_TYPE.TYPE_ERROR,
        message: '错误图片类型,请重新上传',
        file,
      }
    }
    if (!validFileSize(file, maxSize)) {
      return {
        type: UPLOAD_ERROR_TYPE.SIZE_ERROR,
        message: `图片大小超出${maxSize}M限制,请重新上传`,
        file,
      }
    }
    if (hasLimitPixel) {
      const isValidWidth = await validFileWidthHeight(file)
      if (!isValidWidth) {
        return {
          type: UPLOAD_ERROR_TYPE.PIXEL_SIZE_ERROR,
          message: '该图片分辨率过大,请压缩后上传',
          file,
        }
      }
    }
  }

  const onErrorMessage = (errorFile) => {
    if (errorFile.length) {
      const mesText = new Array(...new Set([...errorFile.map(item => item.message)]))
      mesText.length && message.error(mesText)
      onError?.(errorFile)
    }
  }

  const onChange = async (_files: File[]) => {
    const ossResult: OssResult = await request.get(GET_TOKEN_API, { params: { dir: customDir || CDN_DIR } })
    const isValidPromises = []
    let files = _files
    if (onBefore) {
      const result = onBefore(_files)
      if (result) {
        files = result.files
        if (!result.success) {
          return
        }
      }
    }
    if (files.length > maxCount) {
      message.error(`超出${maxCount}最大数量,请重新上传`)
      return
    }
    onStartUpload?.()
    for (const file of files) {
      isValidPromises.push(isValid(file))
    }
    Promise.all(isValidPromises).then((result) => {
      if (result.filter(Boolean).length) {
        onErrorMessage(result.filter(Boolean))
        onEndUpload?.()
      } else {
        const uploadPromiseAll = []
        const successFile = []
        const errorFile = []
        const progress = new Map()
        for (const file of files) {
          const onProgress = (e, key, file) => {
            progress.set(key, {
              event: e,
              key,
              file
            })
            onUploadProgress?.([...progress.values()], files)
          }
          const uploadImagePromise = getUploadToOSSSuccess(
            file,
            customDir,
            ossResult?.sign,
            onProgress,
            (data: UploadSuccessResult) => {
              successFile.push(data)
            },
            () => {
              errorFile.push({
                type: UPLOAD_ERROR_TYPE.UPLOAD_ERROR,
                message: '上传失败,请重新上传',
                file
              })
            }
          )
          uploadPromiseAll.push(uploadImagePromise)
          uploadPromiseAll.push(uploadImagePromise)
        }
        Promise.all(uploadPromiseAll).then(() => {
          onSuccess?.(successFile)
          onErrorMessage(errorFile)
          onEndUpload?.()
        })
      }
    })
  }

  return <Upload
    accept=".jpg,.jpeg,.png"
    directory={directory}
    multiple={multiple}
    disabled={disabled}
    onChange={onChange}
    onClick={onClick}
    style={style}
  >
    {children}
  </Upload>
}
